import moment from "moment";
import { omit } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { BookingStatusType, BookingWeekQueryInput } from "@graphql";

export enum BookingFilterGroup {
  Finance = "finance",
  Schedule = "schedule",
  Sync = "sync",
  Audit = "audit",
  KitSupport = "kitSupport"
}

export const groupBy = (): Array<{ label: string; value: BookingFilterGroup }> => [
  { label: locale.Finance, value: BookingFilterGroup.Finance },
  { label: locale.Schedule, value: BookingFilterGroup.Schedule },
  { label: locale.Sync, value: BookingFilterGroup.Sync },
  { label: locale.Audit, value: BookingFilterGroup.Audit }
];

export type Filter = Omit<
  BookingWeekQueryInput,
  "costCenter" | "consultantIds" | "customerIds"
> & {
  costCenter?: Array<string | null>;
  consultants: LabelValue[];
  customers: LabelValue[];
  /**
   * Where the demand came from — the platform filter an HBA uses to work one
   * platform at a time (ADR-0008, TR-4). `Empty` selects the weeks nobody
   * attributed.
   *
   * Declared here rather than inherited from `BookingWeekQueryInput`: the
   * generated input lags a schema change by one `generate-graphql-client` run,
   * which needs a live API server, and the field would otherwise be
   * unreferenceable until someone boots one. It is spread straight into the
   * query input, so it reaches the resolver either way.
   */
  demandSource?: string[];
};

export const getInitialFilter = (): Filter => ({
  areasOfExpertise: [],
  bookingStatus: [
    BookingStatusType.Billed,
    BookingStatusType.Closed,
    BookingStatusType.Open,
    BookingStatusType.Paid
  ],
  consultants: [],
  costCenter: [],
  customers: [],
  customerNumber: "",
  callNumber: "",
  customerGroup: [],
  from: moment.utc().startOf("isoWeek").subtract(5, "weeks").toDate(),
  invoiceNumber: "",
  patientId: "",
  organizationType: [],
  projectId: [],
  region: [],
  responsiblePersonId: [],
  projectResponsiblePersonId: [],
  specialization: [],
  text: "",
  timeReportStatus: [],
  timeReportStatusSetBy: [],
  assignedPersons: [],
  to: moment.utc().endOf("isoWeek").add(20, "weeks").toDate(),
  systems: [],
  accommodationStatus: [],
  travelStatus: [],
  bookingSupportTag: [],
  demandSource: [],
  groupInvoicing: "null"
});

export type Header = {
  week: string;
  year: string;
};

export type BaseRowData = {
  _id: string;
  Week: Date;
};

export type Item<RowData extends BaseRowData> = {
  _id: string;
  data: RowData;
  header: Header;
};

export type Row<ITEM> =
  | (ITEM & { type: "card" })
  | {
      _id: string;
      type: "group";
      header: Header;
    };

function getHeader(item: BaseRowData): Header {
  const m = moment.utc(item.Week);
  const year = m.format("GGGG");
  const week = m.format("W");

  return { week, year };
}

export function parseItems<RowData extends BaseRowData>(
  last: Row<Item<RowData>>,
  items: RowData[]
): Array<Row<Item<RowData>>> {
  const result: Array<Row<Item<RowData>>> = [];

  const _items: Array<Item<RowData>> = items.map((item) => ({
    _id: item._id,
    data: item,
    header: getHeader(item)
  }));
  const hasPreviousItems = !!last;
  let prev = hasPreviousItems ? headerToString(last.header) : "";

  _items.forEach((item, i) => {
    const group = headerToString(item.header);
    const isNewGroup = prev !== group;
    const isFirstItem = i === 0;
    if (isNewGroup) {
      if (!isFirstItem) {
        result.push({
          _id: group,
          type: "group",
          header: getHeader(item.data),
          ...omit(item, "_id")
        });
      }

      prev = group;
    }

    result.push({ ...item, type: "card" });
  });

  return result;
}

const headerToString = (header: Header) => `${header.year} ${header.week}`;

export const optAllYesNo = [
  {
    label: locale.All,
    value: "null"
  },
  {
    label: locale.Yes,
    value: "true"
  },
  {
    label: locale.No,
    value: "false"
  }
];
